import { Permission } from 'constants/permissions'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { updateBearerToken } from 'utils/api'
import { getAnalytics, setUserId } from 'firebase/analytics'
import { AuthHook, User, LogoutOptions } from '../types'
import { USER_AUTHORIZATION_KEY, ORG_ID_KEY } from '../utils/authConstants'
import type { User as Auth0User } from '@auth0/auth0-react'

/**
 * ID Token Claims (internal type for Auth0)
 * Contains user information and permissions from Auth0
 */
interface IdTokenClaims {
  /** Standard JWT claims */
  sub?: string
  email?: string
  name?: string
  /** Custom claims - flexible for Auth0 */
  [key: string]: unknown
}

/**
 * useAuth0Auth
 *
 * Hook for Auth0-based authentication (cloud deployments).
 * Integrates with Auth0 for user authentication, token management, and permission handling.
 */
export default function useAuth0Auth(): AuthHook {
  const auth0 = useAuth0()
  const [permissionsLoaded, setPermissionsLoaded] = useState(false)
  const [permissions, setPermissions] = useState<Set<Permission>>(new Set())

  // Initialize authentication when user becomes authenticated
  useEffect(() => {
    if (!auth0.isAuthenticated || !auth0.user) {
      return
    }

    const user = auth0.user as Auth0User

    async function updateToken(ignoreCache: boolean): Promise<void> {
      const token = await auth0.getAccessTokenSilently({ ignoreCache })
      updateBearerToken(token)
    }

    function isMissingPermissions(claims: IdTokenClaims): boolean {
      const authData = claims[USER_AUTHORIZATION_KEY] as { permissions?: Permission[] } | undefined
      return !authData || !authData.permissions || authData.permissions.length === 0
    }

    async function getClaims(): Promise<IdTokenClaims | undefined> {
      const claims = await auth0.getIdTokenClaims()
      if (!claims) return undefined

      const result = claims as IdTokenClaims
      const authData = result[USER_AUTHORIZATION_KEY] as { permissions?: Permission[] } | undefined
      if (authData?.permissions) {
        setPermissions(new Set(authData.permissions))
      }

      return result
    }

    async function initialize() {
      // Store org_id in localStorage
      if (user.org_id) {
        localStorage.setItem(ORG_ID_KEY, user.org_id)
        if (process.env.NODE_ENV === 'development') {
          localStorage.setItem('currentUser', JSON.stringify(user))
        }
      }

      // Set user ID for analytics
      const analytics = getAnalytics()
      if (user.email) {
        setUserId(analytics, user.email)
      }

      // Load tokens and permissions
      await updateToken(false)
      const claims = await getClaims()

      // Check if we have a role but no permissions - if so this may be the first signup where we just assigned
      // a default role. Silently do another token refresh
      // https://community.auth0.com/t/how-do-i-add-a-default-role-permissions-to-a-user/43269/5
      //
      // Note that from reviewing the Auth0 code, refreshing the access token silently also updates the id token
      // So we should get updated permissions in both the ID Token and Access Token after this
      if (!claims || isMissingPermissions(claims)) {
        await updateToken(true)
        await getClaims()

        // @TODO Handle the case where the user is still missing permissions for some reason with some
        // UI feedback. This should usually not happen, but there may be a mistake in the Auth0 config
      }

      if (claims && !isMissingPermissions(claims)) {
        setPermissionsLoaded(true)
      }
    }

    initialize()
  }, [auth0])

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return permissions.has(permission)
    },
    [permissions]
  )

  const loginWithRedirect = useCallback(
    async (options?: { returnTo?: string; organization?: string }): Promise<void> => {
      const auth0Options = {
        appState: options?.returnTo ? { returnTo: options.returnTo } : undefined,
        organization: options?.organization,
      }
      return auth0.loginWithRedirect(auth0Options)
    },
    [auth0]
  )

  const logout = useCallback(
    (options?: LogoutOptions): void => {
      localStorage.removeItem(ORG_ID_KEY)
      auth0.logout(options)
    },
    [auth0]
  )

  const user: User | null = useMemo(() => {
    const auth0User = auth0.user as Auth0User | undefined
    if (!auth0User) return null

    return {
      email: auth0User.email,
      name: auth0User.name,
      org_id: auth0User.org_id,
      sub: auth0User.sub,
      ...auth0User,
    }
  }, [auth0.user])

  return useMemo(
    () => ({
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading || (auth0.isAuthenticated && !permissionsLoaded),
      user,
      error: auth0.error,
      isInternalUser: permissions.has('deepcell_internal:read'),
      supportsLogin: true,
      hasPermission,
      loginWithRedirect,
      logout,
    }),
    [
      auth0.isAuthenticated,
      auth0.isLoading,
      auth0.error,
      user,
      permissions,
      permissionsLoaded,
      hasPermission,
      loginWithRedirect,
      logout,
    ]
  )
}
